import DOMAIN from 'assets/domains.json';

/**
 * Per-domain UI accent. Neutrals and semantic colours (success / error /
 * disabled) stay shared — only the primary accent follows the tenant brand.
 *
 * Hex values are sampled from each domain's logo where one exists; Aliro
 * uses the canonical product purple from the design guidelines.
 */
export const ALIRO_PRIMARY = '#6025D8';

export interface DomainPalette {
    primary: string;
    primaryDark: string;
    primaryLight: string;
    primaryMuted: string;
    onPrimary: string;
    surface: string;
    surfaceRaised: string;
}

export type DomainPaletteCssVars = {
    '--profile-primary': string;
    '--profile-primary-dark': string;
    '--profile-primary-light': string;
    '--profile-primary-muted': string;
    '--profile-on-primary': string;
    '--profile-surface': string;
    '--profile-surface-raised': string;
};

const DOMAIN_PRIMARY_MAP: Record<string, string> = {
    [DOMAIN.ALIRO]: ALIRO_PRIMARY,
    [DOMAIN.DEMO]: ALIRO_PRIMARY,
    [DOMAIN.FEATURE]: ALIRO_PRIMARY,
    [DOMAIN.LOCAL]: ALIRO_PRIMARY,
    // [DOMAIN.MEDIACRUSH]: '#C8102E',
    // [DOMAIN.UISAUSTRALIA]: '#2B85FF',
    // [DOMAIN.UIS_AUSTRALIA]: '#2B85FF',
    // [DOMAIN.UISUK]: '#213AA1',
    // [DOMAIN.AUSSIEMIGRATE]: '#C8960A',
    // [DOMAIN.SANDBOX]: '#C8960A',
    // [DOMAIN.EASY]: '#C8960A',
    // [DOMAIN.LOOKNOFURTHER]: '#0F172A',
    // [DOMAIN.GREATNORTHVISA]: '#C4161C',
    // [DOMAIN.BIZBUDDY]: '#1D4ED8',
    // [DOMAIN.MDCPORTAL]: '#1B4F72',
    // [DOMAIN.BASTIN]: '#1E3A5F',
    // [DOMAIN.RESOLVED]: '#0F766E',
    // [DOMAIN.TEAMSTAFFINGINTERNATIONAL]: '#0F766E',
    // [DOMAIN.JOSHUASLAYEN]: '#1E40AF',
    // [DOMAIN.NEWDAYIMMIGRATION]: '#0369A1',
    // [DOMAIN.GLOBALCITIZENS]: '#0F766E',
    [DOMAIN.ALIROAUSTRALIA]: ALIRO_PRIMARY,
    [DOMAIN.ALIROCANADA]: ALIRO_PRIMARY,
    [DOMAIN.ALIROUK]: ALIRO_PRIMARY,
    [DOMAIN.MEDIACRUSH]: ALIRO_PRIMARY,
    [DOMAIN.UISAUSTRALIA]: ALIRO_PRIMARY,
    [DOMAIN.UIS_AUSTRALIA]: ALIRO_PRIMARY,
    [DOMAIN.UISUK]: ALIRO_PRIMARY,
    [DOMAIN.AUSSIEMIGRATE]: ALIRO_PRIMARY,
    [DOMAIN.SANDBOX]: ALIRO_PRIMARY,
    [DOMAIN.EASY]: ALIRO_PRIMARY,
    [DOMAIN.LOOKNOFURTHER]: ALIRO_PRIMARY,
    [DOMAIN.GREATNORTHVISA]: ALIRO_PRIMARY,
    [DOMAIN.BIZBUDDY]: ALIRO_PRIMARY,
    [DOMAIN.MDCPORTAL]: ALIRO_PRIMARY,
    [DOMAIN.BASTIN]: ALIRO_PRIMARY,
    [DOMAIN.RESOLVED]: ALIRO_PRIMARY,
    [DOMAIN.TEAMSTAFFINGINTERNATIONAL]: ALIRO_PRIMARY,
    [DOMAIN.JOSHUASLAYEN]: ALIRO_PRIMARY,
    [DOMAIN.NEWDAYIMMIGRATION]: ALIRO_PRIMARY,
    [DOMAIN.GLOBALCITIZENS]: ALIRO_PRIMARY,
};

const THEME_ALIASES: Record<string, string> = {
    aliro: DOMAIN.ALIRO,
    aliroca: DOMAIN.ALIROCANADA,
    alirocanada: DOMAIN.ALIROCANADA,
    aliroau: DOMAIN.ALIROAUSTRALIA,
    aliroaustralia: DOMAIN.ALIROAUSTRALIA,
    alirouk: DOMAIN.ALIROUK,
    uis: DOMAIN.MEDIACRUSH,
    uiscanada: DOMAIN.MEDIACRUSH,
    mediacrush: DOMAIN.MEDIACRUSH,
    uisau: DOMAIN.UISAUSTRALIA,
    uisaustralia: DOMAIN.UISAUSTRALIA,
    uisuk: DOMAIN.UISUK,
    aussie: DOMAIN.AUSSIEMIGRATE,
    aussiemigrate: DOMAIN.AUSSIEMIGRATE,
    lnf: DOMAIN.LOOKNOFURTHER,
    looknofurther: DOMAIN.LOOKNOFURTHER,
    gnv: DOMAIN.GREATNORTHVISA,
    greatnorthvisa: DOMAIN.GREATNORTHVISA,
    bizbuddy: DOMAIN.BIZBUDDY,
    mdc: DOMAIN.MDCPORTAL,
    bastin: DOMAIN.BASTIN,
    resolved: DOMAIN.RESOLVED,
    tsi: DOMAIN.TEAMSTAFFINGINTERNATIONAL,
    joshua: DOMAIN.JOSHUASLAYEN,
    newday: DOMAIN.NEWDAYIMMIGRATION,
    globalcitizens: DOMAIN.GLOBALCITIZENS,
};

export function parseHex(hex: string): [number, number, number] {
    const normalized = hex.replace('#', '').trim();
    const full =
        normalized.length === 3
            ? normalized
                  .split('')
                  .map((char) => char + char)
                  .join('')
            : normalized.slice(0, 6);
    const value = parseInt(full, 16);
    if (Number.isNaN(value) || full.length !== 6) {
        return parseHex(ALIRO_PRIMARY);
    }
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex(red: number, green: number, blue: number): string {
    const clamp = (channel: number) => Math.max(0, Math.min(255, Math.round(channel)));
    return `#${[clamp(red), clamp(green), clamp(blue)]
        .map((channel) => channel.toString(16).padStart(2, '0'))
        .join('')}`;
}

export function mixHex(hex: string, toward: string, amount: number): string {
    const [red, green, blue] = parseHex(hex);
    const [towardRed, towardGreen, towardBlue] = parseHex(toward);
    return toHex(
        red + (towardRed - red) * amount,
        green + (towardGreen - green) * amount,
        blue + (towardBlue - blue) * amount
    );
}

export function withAlpha(hex: string, alpha: number): string {
    const [red, green, blue] = parseHex(hex);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function contrastText(hex: string): string {
    const [red, green, blue] = parseHex(hex).map((channel) => channel / 255) as [
        number,
        number,
        number,
    ];
    const toLinear = (channel: number) =>
        channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    const luminance = 0.2126 * toLinear(red) + 0.7152 * toLinear(green) + 0.0722 * toLinear(blue);
    return luminance > 0.45 ? '#0f172a' : '#ffffff';
}

export function buildDomainPalette(primary: string): DomainPalette {
    return {
        primary,
        primaryDark: mixHex(primary, '#000000', 0.22),
        primaryLight: mixHex(primary, '#ffffff', 0.32),
        primaryMuted: withAlpha(primary, 0.1),
        onPrimary: contrastText(primary),
        surface: '#F8FAFC',
        surfaceRaised: '#ffffff',
    };
}

export function getDomainPrimary(domainKey: string): string {
    return DOMAIN_PRIMARY_MAP[domainKey] ?? ALIRO_PRIMARY;
}

export function getDomainPalette(domainKey: string): DomainPalette {
    return buildDomainPalette(getDomainPrimary(domainKey));
}

export function getDomainPaletteCssVars(palette: DomainPalette): DomainPaletteCssVars {
    return {
        '--profile-primary': palette.primary,
        '--profile-primary-dark': palette.primaryDark,
        '--profile-primary-light': palette.primaryLight,
        '--profile-primary-muted': palette.primaryMuted,
        '--profile-on-primary': palette.onPrimary,
        '--profile-surface': palette.surface,
        '--profile-surface-raised': palette.surfaceRaised,
    };
}

export function resolveThemeAlias(raw: string): string | undefined {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.toLowerCase() === 'default') {
        return undefined;
    }

    const upper = trimmed.toUpperCase().replace(/[-\s]/g, '_');
    if (upper in DOMAIN_PRIMARY_MAP) {
        return upper;
    }

    return THEME_ALIASES[trimmed.toLowerCase()];
}
